<template>
  <vams-collapse
    id="billing-and-payment-collapse"
    :ref="'billing-and-payment'"
    title="Billing and Payment"
    :visible="visible"
  >
    <b-row no-gutters>
      <b-col class="customer-info-column">
        <div style="margin-bottom: 10px">BILL TO:</div>
        <b-row>
          <b-col>
            <div>
              <div>
                <router-link v-if="!$isCustomer" :to="'/customers/' + billToCustomer.customerId">
                  {{ billToCustomer.customerId }} {{ serviceOrder.billToCustomerName }}
                </router-link>
                <div v-else>{{ billToCustomer.customerId }} {{ billToCustomer.customerName }}</div>
              </div>
            </div>
            <div v-if="isEdit">
              <b-form-group label="Address 1:" label-class="col-head" label-for="address1">
                <b-input-group>
                  <b-input id="address1" v-model="billToCustomerAddr1" class="rounded" />
                </b-input-group>
              </b-form-group>
              <b-form-group label="Address 2:" label-class="col-head" label-for="address2">
                <b-input-group>
                  <b-input id="address2" v-model="billToCustomerAddr2" class="rounded" />
                </b-input-group>
              </b-form-group>
              <b-form-group label="City:" label-class="col-head" label-for="city">
                <b-input-group>
                  <b-input id="city" v-model="billToCustomerCity" class="rounded" />
                </b-input-group>
              </b-form-group>
              <b-form-group label="State:" label-class="col-head" label-for="state">
                <b-input-group>
                  <b-input id="address1" v-model="billToCustomerState" class="rounded" />
                </b-input-group>
              </b-form-group>
              <b-form-group label="Zip Code:" label-class="col-head" label-for="zip">
                <b-input-group>
                  <b-input id="zip" v-model="billToCustomerPost" class="rounded" />
                </b-input-group>
              </b-form-group>
              <b-form-group label="County:" label-class="col-head" label-for="county">
                <b-input-group>
                  <b-input id="county" v-model="billToCustomerCounty" class="rounded" />
                </b-input-group>
              </b-form-group>
              <b-button @click="toggleEdit">Close Edit Address</b-button>
            </div>
            <div v-else>
              <div>{{ serviceOrder.billToCustomerAddr1 }}</div>
              <div>{{ serviceOrder.billToCustomerAddr2 }}</div>
              <div>
                {{ serviceOrder.billToCustomerCity }}, {{ serviceOrder.billToCustomerState }}
                {{ serviceOrder.billToCustomerPost }}
              </div>
              <div>
                {{ serviceOrder.billToCustomerCounty }}
              </div>
              <b-button @click="toggleEdit">Edit Address</b-button>
              <div class="customer-phone">
                {{ billToCustomer.phone | phone }}
              </div>
            </div>
            <b-badge v-if="customer.poRequired" class="po-required-badge mb-2" variant="danger">
              <span>PO Required</span>
            </b-badge>
          </b-col>
          <b-col>
            <b-form-group v-if="!readonly && !$isCustomer" style="width: 150px">
              <label class="col-head">Available Credit</label>
              <div :class="isCreditRed" class="available-credit">
                {{ billToCustomer.availableCredit | currency }}
              </div>
            </b-form-group>
            <b-form-group>
              <label class="col-head">Terms</label>
              <div>{{ billToCustomer.termId }}</div>
            </b-form-group>
            <b-form-group v-if="!$isCustomer" label="PO #" label-class="col-head" label-for="purchase-order">
              <template #label>
                PO #
                <sup>
                  <font-awesome-icon
                    id="purchase_order_tooltip"
                    icon="info-circle"
                    size="lg"
                    class="mx-1 text-primary align-self-center"
                    display="inline-block"
                  />
                  <b-tooltip target="purchase_order_tooltip" triggers="hover">
                    Updated by {{ poUpdatedByType }}
                    <div>{{ poUpdatedByName }}</div>
                    <div>{{ datePoUpdated | dateTime }}</div>
                  </b-tooltip>
                </sup>
              </template>
              <div v-if="readonly" id="purchase-order">
                <div>
                  {{ purchaseOrder | nullValueToNA }}
                </div>
              </div>
              <b-input-group v-else>
                <b-form-input id="purchase-order" v-model="purchaseOrder" maxlength="30" />
                <font-awesome-icon
                  v-show="isPOMismatch && !loadingLinkedOrder"
                  id="po-mismatch"
                  icon="exclamation-circle"
                  size="lg"
                  fixed-width
                  class="mx-1 text-primary align-self-center text-danger"
                  display="inline-block"
                />
                <b-tooltip target="po-mismatch" triggers="hover">
                  {{ poUpdateWarningText }}
                </b-tooltip>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row v-if="!readonly && isCsrOrCsm" style="margin-top: 20px">
          <b-col>
            <div v-if="!changing">
              <b-button variant="secondary" :disabled="isEdit" @click="startChange">Change Bill-To</b-button>
            </div>
            <div v-else>
              <customer-search-input v-model="newBillToCustomer" label="New Bill-To Customer" @input="selectCustomer" />
              <div>
                <b-button variant="primary" class="mr-3" @click="confirmSave">Save</b-button>
                <b-button variant="secondary" @click="cancel">Cancel</b-button>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-col>
      <b-col v-if="canViewPaymentRequest && !$isCustomer" style="border-left: 1px solid lightgray; padding-left: 15px">
        <e-pay-component
          :order-id="serviceOrderId"
          :invoice-order-id="invoiceServiceOrderId"
          :order="serviceOrder"
          @updateServiceOrder="updateServiceOrder"
        />
      </b-col>
      <b-col v-else></b-col>
    </b-row>
    <unsaved-changes-modal
      ref="ConfirmSaveModal"
      title="Save Changes"
      :message="confirmMessage"
      continue-btn-text="Change Bill-To"
      cancel-btn-text="Discard"
    />
  </vams-collapse>
</template>

<script>
// components
import UnsavedChangesModal from '@/shared/components/UnsavedChangesModal';
import CollapseComponent from '@/shared/components/ui/CollapseComponent';
import CustomerSearchInput from '@/shared/components/customer/CustomerSearchInput';
import EPayComponent from '@/shared/components/ui/EPayComponent';
// vuex
import { mapActions, mapGetters } from 'vuex';
import { UserGetters } from '@/shared/store/user/types';
import { CustomerActions } from '@/shared/store/customer/types';
import { ServiceOrderActions, ServiceOrderGetters, ServiceOrderMutations } from '@/shared/store/service-order/types';
import { FORM_STAGES } from '@/shared/store/service-order/state';
// helpers
import ErrorService from '@/shared/services/ErrorService';
import SuccessService from '@/shared/services/SuccessService';

export default {
  name: 'BillingAndPaymentComponent',
  components: {
    'unsaved-changes-modal': UnsavedChangesModal,
    'vams-collapse': CollapseComponent,
    'customer-search-input': CustomerSearchInput,
    'e-pay-component': EPayComponent
  },
  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    visible: Boolean()
  },
  data: function () {
    return {
      FORM_STAGES: FORM_STAGES,
      changing: false,
      isEdit: false
    };
  },
  computed: {
    ...mapGetters({
      authorizeRole: UserGetters.AUTHORIZE_ROLE,
      customer: ServiceOrderGetters.GET_CUSTOMER,
      stage: ServiceOrderGetters.GET_FORM_STAGE,
      serviceOrderId: ServiceOrderGetters.GET_SERVICE_ORDER_ID,
      invoiceServiceOrderId: ServiceOrderGetters.GET_INVOICE_SERVICE_ORDER_ID,
      estimorderDisplayText: ServiceOrderGetters.GET_ESTIMORDER_DISPLAY_TEXT,
      serviceOrder: ServiceOrderGetters.GET_SERVICE_ORDER,
      isPOMismatch: ServiceOrderGetters.GET_IS_PO_MISMATCH,
      loadingLinkedOrder: ServiceOrderGetters.GET_LOADING_LINKED_ORDER,
      isEditBillingAddress: ServiceOrderGetters.GET_IS_EDIT_BILLING_ADDRESS
    }),
    canViewPaymentRequest() {
      return (
        this.isCsrOrCsm &&
        (this.serviceOrder.allowEPay || this.serviceOrder.allowEPayInPerson) &&
        !this.serviceOrder.isEstimate
      );
    },
    isCsrOrCsm() {
      return this.authorizeRole(['CSR', 'CSM', 'RegionalCSM']);
    },
    unitId() {
      return this.$route.params.unitId;
    },
    isCreditRed() {
      return this.billToCustomer.availableCredit < 0 ? 'credit-red' : '';
    },
    billToCustomerAddr1: {
      get() {
        return this.serviceOrder.billToCustomerAddr1;
      },
      set(value) {
        this.$store.commit(ServiceOrderMutations.SET_PROP, { key: 'billToCustomerAddr1', value });
      }
    },
    billToCustomerAddr2: {
      get() {
        return this.serviceOrder.billToCustomerAddr2;
      },
      set(value) {
        this.$store.commit(ServiceOrderMutations.SET_PROP, { key: 'billToCustomerAddr2', value });
      }
    },
    billToCustomerCity: {
      get() {
        return this.serviceOrder.billToCustomerCity;
      },
      set(value) {
        this.$store.commit(ServiceOrderMutations.SET_PROP, { key: 'billToCustomerCity', value });
      }
    },
    billToCustomerCounty: {
      get() {
        return this.serviceOrder.billToCustomerCounty;
      },
      set(value) {
        this.$store.commit(ServiceOrderMutations.SET_PROP, { key: 'billToCustomerCounty', value });
      }
    },
    billToCustomerPost: {
      get() {
        return this.serviceOrder.billToCustomerPost;
      },
      set(value) {
        this.$store.commit(ServiceOrderMutations.SET_PROP, { key: 'billToCustomerPost', value });
      }
    },
    billToCustomerState: {
      get() {
        return this.serviceOrder.billToCustomerState;
      },
      set(value) {
        this.$store.commit(ServiceOrderMutations.SET_PROP, { key: 'billToCustomerState', value });
      }
    },
    purchaseOrder: {
      get() {
        return this.$store.getters[ServiceOrderGetters.GET_PURCHASE_ORDER];
      },
      set(value) {
        this.$store.commit(ServiceOrderMutations.SET_PROP, { key: 'purchaseOrder', value });
      }
    },
    billToCustomer: {
      get() {
        return this.$store.getters[ServiceOrderGetters.GET_BILL_TO_CUSTOMER];
      },
      set(value) {
        this.$store.commit(ServiceOrderMutations.SET_PROP, { key: 'billToCustomer', value });
      }
    },
    newBillToCustomer: {
      get() {
        return this.$store.getters[ServiceOrderGetters.GET_NEW_BILL_TO_CUSTOMER];
      },
      set(value) {
        this.$store.commit(ServiceOrderMutations.SET_PROP, { key: 'newBillToCustomer', value });
      }
    },
    confirmMessage() {
      if (this.newBillToCustomer) {
        return `Are you sure you want to change the Bill-To-Customer to ${this.newBillToCustomer.customerId} - ${this.newBillToCustomer.customerName}?`;
      } else {
        return null;
      }
    },
    poUpdatedByType() {
      return this.serviceOrder.poUpdatedByType;
    },
    poUpdatedByName() {
      return this.serviceOrder.poUpdatedByName;
    },
    datePoUpdated() {
      return this.serviceOrder.datePoUpdated;
    },
    poUpdateWarningText() {
      var isEstimate = this.serviceOrderId?.substring(0, 1) === 'E';
      return `PO # entered on ${isEstimate ? 'Service Order' : 'Estimate'} does not match. Saving this ${
        isEstimate ? 'Estimate' : 'Service Order'
      } will overwrite PO # on ${isEstimate ? 'Service Order' : 'Estimate'} `;
    }
  },
  watch: {
    isEditBillingAddress() {
      this.isEdit = this.isEditBillingAddress;
    }
  },
  methods: {
    ...mapActions([
      CustomerActions.FETCH_CUSTOMER,
      ServiceOrderActions.UPDATE_BILL_TO_CUSTOMER,
      ServiceOrderActions.FETCH_CUSTOMER_SPECIFIC_REPAIR_REASONS,
      ServiceOrderActions.FETCH_BILL_TO_CUSTOMER_ALERTS,
      ServiceOrderActions.FETCH_SERVICE_ORDER
    ]),
    async switchStage(newStage) {
      var switchStageSuccess = await this.$store.dispatch(ServiceOrderActions.SWITCH_STAGE, {
        newStage,
        that: this
      });

      return switchStageSuccess;
    },
    toggleEdit() {
      this.isEdit = !this.isEdit;
    },
    startChange() {
      this.changing = true;
    },
    async selectCustomer(customer) {
      this.newBillToCustomer = await this[CustomerActions.FETCH_CUSTOMER](customer.customerId);
    },
    confirmSave() {
      this.$refs.ConfirmSaveModal.show(this, this.save);
    },
    async save(cancel = true) {
      if (cancel) {
        this.cancel();
        return;
      }

      this.billToCustomer = this.newBillToCustomer;

      if (this.stage == FORM_STAGES.EDIT_SO) {
        try {
          this.switchStage(FORM_STAGES.SAVING_SO);
          const response = await this[ServiceOrderActions.UPDATE_BILL_TO_CUSTOMER]({
            unitId: this.unitId,
            serviceOrderId: this.serviceOrderId
          });

          this[ServiceOrderActions.FETCH_CUSTOMER_SPECIFIC_REPAIR_REASONS](this.billToCustomer.customerId);
          this[ServiceOrderActions.FETCH_BILL_TO_CUSTOMER_ALERTS](this.billToCustomer.customerId);
          await this[ServiceOrderActions.FETCH_SERVICE_ORDER]({
            unitId: this.serviceOrder.unitId,
            serviceOrderId: this.serviceOrderId
          });
          SuccessService.createSuccessToast(
            this.$root,
            `${this.estimorderDisplayText} #${response.serviceOrderId} saved successfully.`
          );
        } catch {
          const errorMessage = `Error saving ${this.estimorderDisplayText}.`;
          ErrorService.createErrorToast(this, errorMessage);
          throw Error(errorMessage);
        } finally {
          this.switchStage(FORM_STAGES.EDIT_SO);
        }
      }
      this.cancel();
    },
    cancel() {
      this.changing = false;
      this.newBillToCustomer = null;
    },
    async updateServiceOrder() {
      this.$emit('updateServiceOrder');
    }
  }
};
</script>
